import { useEffect } from "react";
import { useLang, HREFLANG_BY_LANG } from "@/context/LanguageContext";
import { cleanUrl, localizedUrl } from "@/i18n/locale";

// OG locale codes use underscores (BCP-47-ish). Stay aligned with hreflang where possible.
export const OG_LOCALE_BY_LANG = {
  en: "en_US",
  nl: "nl_NL", de: "de_DE", fr: "fr_FR", es: "es_ES", it: "it_IT",
  pt: "pt_PT", pl: "pl_PL", sv: "sv_SE", da: "da_DK", fi: "fi_FI",
  no: "nb_NO", el: "el_GR", cs: "cs_CZ", ro: "ro_RO", hu: "hu_HU",
  sk: "sk_SK", bg: "bg_BG", hr: "hr_HR", sl: "sl_SI", et: "et_EE",
  lv: "lv_LV", lt: "lt_LT",
};

const BASE_URL = "https://horseappcompare.com";
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-default.png`;
const SITE_NAME = "HorseAppCompare";

const absUrl = (u) => {
  if (!u) return DEFAULT_OG_IMAGE;
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  if (u.startsWith("/")) return `${BASE_URL}${u}`;
  return u;
};

// Path-based localized canonical: /de/apps/x — must match LanguageContext + sitemap.
const localizedCanonical = (lang) => {
  if (typeof window === "undefined") return BASE_URL;
  return localizedUrl(cleanUrl(window.location.href), lang);
};

const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("data-social", "1");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

const removePrevious = () => {
  Array.from(document.head.querySelectorAll('meta[data-social="1"]')).forEach((el) => el.remove());
};

/**
 * useSocialMeta — inject OG + Twitter Card meta tags for the current page, localized to the active language.
 *
 * @param {object} cfg
 * @param {string} cfg.title          Page title (already translated by the caller)
 * @param {string} cfg.description    Page description (already translated)
 * @param {string} [cfg.image]        Absolute or relative image URL (defaults to /og-default.png)
 * @param {"website"|"article"|"profile"} [cfg.type="website"]
 * @param {object} [cfg.article]      Optional article props (published_time, modified_time, section, tags[])
 */
export const useSocialMeta = ({ title, description, image, type = "website", article } = {}) => {
  const { lang } = useLang();

  useEffect(() => {
    if (typeof document === "undefined" || !title) return;

    removePrevious();
    const url = localizedCanonical(lang);
    const ogLocale = OG_LOCALE_BY_LANG[lang] || "en_US";
    const imgAbs = absUrl(image);

    // Standard meta
    document.title = title;
    upsertMeta('meta[name="description"][data-social="1"]', { name: "description", content: description || "" });

    // Open Graph
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: description || "" });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: url });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: type });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: ogLocale });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: imgAbs });
    upsertMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: title });

    // og:locale:alternate for every other language → tells Facebook which alternates exist
    Object.entries(OG_LOCALE_BY_LANG).forEach(([code, ogl]) => {
      if (code === lang) return;
      const meta = document.createElement("meta");
      meta.setAttribute("property", "og:locale:alternate");
      meta.setAttribute("content", ogl);
      meta.setAttribute("data-social", "1");
      document.head.appendChild(meta);
    });

    // Twitter Card
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description || "" });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: imgAbs });
    upsertMeta('meta[name="twitter:image:alt"]', { name: "twitter:image:alt", content: title });
    upsertMeta('meta[name="twitter:site"]', { name: "twitter:site", content: "@horseappcompare" });

    // Article-specific OG props
    if (type === "article" && article) {
      if (article.published_time) upsertMeta('meta[property="article:published_time"]', { property: "article:published_time", content: article.published_time });
      if (article.modified_time)  upsertMeta('meta[property="article:modified_time"]',  { property: "article:modified_time",  content: article.modified_time });
      if (article.section)        upsertMeta('meta[property="article:section"]',        { property: "article:section",        content: article.section });
      (article.tags || []).slice(0, 6).forEach((tag) => {
        const meta = document.createElement("meta");
        meta.setAttribute("property", "article:tag");
        meta.setAttribute("content", tag);
        meta.setAttribute("data-social", "1");
        document.head.appendChild(meta);
      });
    }

    return () => removePrevious();
  }, [title, description, image, type, lang, article]);

  // expose hreflang map for callers that want to render link rel="alternate" themselves
  return { ogLocale: OG_LOCALE_BY_LANG[lang], hreflang: HREFLANG_BY_LANG[lang] };
};

export default useSocialMeta;
